import { Label } from "@radix-ui/react-label";
import { t } from "i18next";
import { AlignLeft, Archive, Languages } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { TranscriptionDefaultFormFieldsName } from "@/common/enums/transcriptionDefaultFormFields";
import type { VisitRetentionPeriod } from "@/common/types/settingsOptions";
import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  languageDisplayName,
  writeSameAsConversation,
} from "@/lib/note-language";
import type { TranscriptionSchemaType } from "@/schemas/transcription.schema";
import { useAuthStore } from "@/store/auth.store";
import { SETTINGS_FIELD_LABEL, SettingsCard } from "../redesign/SettingsCard";

interface Props {
  onChange: (data: Partial<TranscriptionSchemaType>) => void;
  isSubmitting: boolean;
  loading: boolean;
  data: {
    transcriptionLanguages: TranscriptionLanguage[];
    styles: {
      value: string;
      label: string;
    }[];
    formats: {
      value: string;
      label: string;
    }[];
    visitRetentionPeriods: VisitRetentionPeriod[];
  };
}

export const TranscriptionDefaultFormFields = ({
  data: { transcriptionLanguages, styles, visitRetentionPeriods, formats },
  ...props
}: Props): React.ReactNode => {
  const { setValue, getValues } = useFormContext<TranscriptionSchemaType>();
  const { i18n } = useTranslation();

  const languageSelect = (
    <Select
      disabled={props.isSubmitting || props.loading}
      name={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
      value={getValues(
        TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE,
      )}
      onValueChange={(value) => {
        const selected = transcriptionLanguages.find(
          (lang) => lang.name === value,
        );
        if (selected) {
          // A language picked here is the note language from now on: it
          // turns off "Same as conversation" (New visit, workspace), which
          // would otherwise replace it at the next visit.
          writeSameAsConversation(
            useAuthStore.getState().userData?.public_id,
            false,
          );
          setValue("transcriptionLanguageId", selected.id);
          props.onChange({ transcriptionLanguageId: selected.id });
        }
      }}
    >
      <SelectTrigger
        aria-describedby="settings-note-language-hint"
        id={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
      >
        <SelectValue
          placeholder={t(
            "settings.transcription.placeholderTranscriptionLanguage",
          )}
        />
      </SelectTrigger>
      <SelectContent>
        {transcriptionLanguages.map((lang) => (
          <SelectItem key={lang.id} value={lang.name}>
            {languageDisplayName(
              lang.name,
              i18n?.language,
              t("noteLanguage.mandarin"),
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const styleSelect = (
    <Select
      disabled={props.isSubmitting}
      name={TranscriptionDefaultFormFieldsName.STYLE}
      value={getValues(TranscriptionDefaultFormFieldsName.STYLE)}
      onValueChange={(value) => {
        props.onChange({ style: value });
      }}
    >
      <SelectTrigger id={TranscriptionDefaultFormFieldsName.STYLE}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {styles.map((lang) => (
          <SelectItem key={lang.value} value={lang.value}>
            {lang.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const formatSelect = (
    <Select
      disabled={props.isSubmitting}
      name={TranscriptionDefaultFormFieldsName.FORMAT}
      value={getValues(TranscriptionDefaultFormFieldsName.FORMAT)}
      onValueChange={(value) => {
        setValue(TranscriptionDefaultFormFieldsName.FORMAT, value);
        props.onChange({ format: value });
      }}
    >
      <SelectTrigger id={TranscriptionDefaultFormFieldsName.FORMAT}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {formats.map((lang) => (
          <SelectItem key={lang.value} value={lang.value}>
            {lang.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const retentionSelect = (
    <Select
      disabled={props.isSubmitting || props.loading}
      name={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
      value={getValues(
        TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD,
      )}
      onValueChange={(value) => {
        const selected = visitRetentionPeriods.find(
          (visitRetention) => visitRetention.label === value,
        );
        if (selected) {
          setValue(
            TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD_ID,
            selected.id,
          );
          props.onChange({ visitRetentionPeriodId: selected.id });
        }
      }}
    >
      <SelectTrigger
        id={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
      >
        <SelectValue
          placeholder={t("settings.transcription.placeholderRetentionPeriod")}
        />
      </SelectTrigger>
      <SelectContent>
        {visitRetentionPeriods.map((visitRetention) => (
          <SelectItem key={visitRetention.id} value={visitRetention.label}>
            {visitRetention.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (AppConfig.enableAppRedesign)
    return (
      <>
        <SettingsCard
          description={
            <span id="settings-note-language-hint">
              {t("settings.transcription.noteLanguageHint")}
            </span>
          }
          icon={<Languages aria-hidden />}
          labelledBy="settings-note-language-title"
          testId="settings-note-language"
          title={t("settings.transcription.cards.noteLanguage")}
        >
          <Label
            className="sr-only"
            htmlFor={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
          >
            {t("settings.transcription.defaultTranscriptionLanguage")}
          </Label>
          <div className="sm:max-w-sm">{languageSelect}</div>
        </SettingsCard>
        <SettingsCard
          description={t("settings.transcription.cards.styleHint")}
          icon={<AlignLeft aria-hidden />}
          labelledBy="settings-note-style-title"
          testId="settings-note-style"
          title={t("settings.transcription.cards.style")}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label
                className={SETTINGS_FIELD_LABEL}
                htmlFor={TranscriptionDefaultFormFieldsName.STYLE}
              >
                {t("settings.transcription.defaultStyle")}
              </Label>
              {styleSelect}
            </div>
            <div>
              <Label
                className={SETTINGS_FIELD_LABEL}
                htmlFor={TranscriptionDefaultFormFieldsName.FORMAT}
              >
                {t("settings.transcription.defaultFormat")}
              </Label>
              {formatSelect}
            </div>
          </div>
        </SettingsCard>
        <SettingsCard
          description={t("settings.transcription.cards.retentionHint")}
          icon={<Archive aria-hidden />}
          labelledBy="settings-retention-title"
          testId="settings-retention"
          title={t("settings.transcription.defaultRetentionPeriod")}
        >
          <Label
            className="sr-only"
            htmlFor={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
          >
            {t("settings.transcription.defaultRetentionPeriod")}
          </Label>
          <div className="sm:max-w-sm">{retentionSelect}</div>
        </SettingsCard>
      </>
    );

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div
        className="space-y-2 sm:col-span-2"
        data-testid="settings-note-language"
      >
        <Label
          htmlFor={TranscriptionDefaultFormFieldsName.TRANSCRIPTION_LANGUAGE}
        >
          {t("settings.transcription.defaultTranscriptionLanguage")}
        </Label>
        {languageSelect}
        <p
          className="text-xs text-muted-foreground"
          id="settings-note-language-hint"
        >
          {t("settings.transcription.noteLanguageHint")}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor={TranscriptionDefaultFormFieldsName.STYLE}>
          {t("settings.transcription.defaultStyle")}
        </Label>
        {styleSelect}
      </div>
      <div className="space-y-2">
        <Label htmlFor={TranscriptionDefaultFormFieldsName.FORMAT}>
          {t("settings.transcription.defaultFormat")}
        </Label>
        {formatSelect}
      </div>
      <div className="space-y-2">
        <Label
          htmlFor={TranscriptionDefaultFormFieldsName.VISIT_RETENTION_PERIOD}
        >
          {t("settings.transcription.defaultRetentionPeriod")}
        </Label>
        {retentionSelect}
      </div>
    </div>
  );
};
